import { useParams, Link, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useOrder, type Order } from '../hooks/useOrders'
import { ArrowLeft, ExternalLink, XCircle, Loader2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

const statusColour: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600',
  awaiting_payment: 'bg-orange-500/10 text-orange-600',
  processing: 'bg-blue-500/10 text-blue-600',
  fulfilling: 'bg-purple-500/10 text-purple-600',
  completed: 'bg-green-500/10 text-green-600',
  delivered: 'bg-green-700/10 text-green-700',
  cancelled: 'bg-red-500/10 text-red-600',
}

const cancellableStatuses = ['pending', 'awaiting_payment', 'processing']

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const paymentStatus = searchParams.get('payment')
  const { data, isLoading, error } = useOrder(id!)
  const qc = useQueryClient()

  const cancelOrder = useMutation<
    { message: string; refunded?: boolean; refundAmount?: number },
    Error,
    string
  >({
    mutationFn: async (orderId) => {
      const res = await fetch(`/api/v1/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Cancel failed' })) as { error?: string }
        throw new Error(body.error || 'Cancel failed')
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', id] })
      qc.invalidateQueries({ queryKey: ['orders'] })
    },
  })

  const order = data?.order as Order | undefined

  if (isLoading) {
    return (
      <div className="container max-w-4xl py-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container max-w-4xl py-8">
        <p className="text-destructive">{error.message}</p>
      </div>
    )
  }

  if (!order) return null

  const verticalData = (() => {
    try { return JSON.parse(order.verticalData || '{}') } catch { return {} }
  })()

  const canCancel = cancellableStatuses.includes(order.status)

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-8 flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/dashboard/orders">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            Order <span className="font-mono">{order.id.slice(0, 12)}...</span>
          </h1>
          <p className="text-muted-foreground capitalize">{order.vertical}</p>
        </div>
        <Badge variant="secondary" className={`ml-auto ${statusColour[order.status] ?? ''}`}>
          {order.status.replace('_', ' ')}
        </Badge>
      </div>

      {paymentStatus === 'success' && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200">
          Payment successful! Your order is now being processed.
        </div>
      )}

      {paymentStatus === 'cancelled' && (
        <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800 dark:border-yellow-900 dark:bg-yellow-950 dark:text-yellow-200">
          Payment was cancelled. You can try again using the link below.
        </div>
      )}

      {cancelOrder.isSuccess && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200">
          Order cancelled.{' '}
          {cancelOrder.data?.refunded && `$${((cancelOrder.data.refundAmount ?? 0) / 100).toFixed(2)} refunded to your credits.`}
        </div>
      )}

      {/* Payment CTA for awaiting_payment orders */}
      {order.status === 'awaiting_payment' && order.paymentUrl && (
        <Card className="mb-6 border-orange-200 dark:border-orange-900">
          <CardContent className="flex items-center justify-between pt-6">
            <div>
              <p className="font-medium">Payment required</p>
              <p className="text-sm text-muted-foreground">
                Complete payment to start processing your order.
              </p>
            </div>
            <Button asChild>
              <a href={order.paymentUrl} target="_blank" rel="noopener noreferrer">
                Pay ${(order.costCents / 100).toFixed(2)}
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Recipient */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recipient</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-sm font-medium">{order.toName}</p>
            <p className="text-sm">{order.toAddressLine1}</p>
            <p className="text-sm">{order.toSuburb} {order.toState} {order.toPostcode}</p>
          </CardContent>
        </Card>

        {/* Sender */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sender</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-sm font-medium">{order.fromName}</p>
          </CardContent>
        </Card>
      </div>

      {/* Cost & Details */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">Order Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <p className="text-sm text-muted-foreground">Total cost</p>
              <p className="text-xl font-bold font-mono">${(order.costCents / 100).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Payment</p>
              <p className="text-sm font-medium capitalize">
                {order.paymentStatus}{order.paymentMethod ? ` (${order.paymentMethod})` : ''}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="text-sm">{new Date(order.createdAt).toLocaleString('en-AU')}</p>
            </div>
          </div>

          {/* Vertical-specific options */}
          {Object.keys(verticalData).length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-2">Options</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(verticalData).map(([key, val]) => {
                  if (typeof val === 'boolean' && val) {
                    return (
                      <Badge key={key} variant="outline" className="capitalize">
                        {key.replace(/_/g, ' ')}
                      </Badge>
                    )
                  }
                  return null
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message */}
      {order.message && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Message</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{order.message}</p>
          </CardContent>
        </Card>
      )}

      {/* Cancel Order */}
      {canCancel && !cancelOrder.isSuccess && (
        <Card className="mt-6 border-destructive/20">
          <CardContent className="flex items-center justify-between pt-6">
            <div>
              <p className="font-medium">Cancel this order</p>
              <p className="text-sm text-muted-foreground">
                Credits will be refunded automatically if already charged.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={cancelOrder.isPending}>
                  {cancelOrder.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="mr-2 h-4 w-4" />
                  )}
                  Cancel Order
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel this order?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will cancel the order and refund any credits charged.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep Order</AlertDialogCancel>
                  <AlertDialogAction onClick={() => cancelOrder.mutate(order.id)}>
                    Cancel Order
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      )}

      {cancelOrder.isError && (
        <p className="text-sm text-destructive mt-4">{cancelOrder.error.message}</p>
      )}
    </div>
  )
}
